/**
 * localStorage 工具函数
 *
 * 从 auth.ts 和 user.ts 中提取的统一存储操作模块，
 * 提供安全的读写删操作以及 JSON 解析能力。
 */

import { logger } from './logger'

/**
 * 从 localStorage 读取值
 *
 * @param key 存储键名
 * @returns 存储的字符串值，读取失败时返回 null
 */
export function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    logger.warn('[Storage] localStorage 读取失败:', e)
    return null
  }
}

/**
 * 向 localStorage 写入值
 *
 * @param key 存储键名
 * @param value 要存储的字符串值
 */
export function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    logger.warn('[Storage] localStorage 写入失败:', e)
  }
}

/**
 * 从 localStorage 删除指定键
 *
 * @param key 要删除的存储键名
 */
export function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch (e) {
    logger.warn('[Storage] localStorage 删除失败:', e)
  }
}

/**
 * 从 localStorage 读取并解析 JSON 值
 *
 * 泛型函数，支持任意类型的 JSON 反序列化。
 * 解析失败时会自动删除损坏的数据并返回 null。
 *
 * @param key 存储键名
 * @returns 解析后的对象，读取或解析失败时返回 null
 *
 * @example
 * ```typescript
 * const user = parseStoredJson<User>('userInfo')
 * if (user) {
 *   console.log(user.name)
 * }
 * ```
 */
export function parseStoredJson<T>(key: string): T | null {
  const raw = readStorage(key)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as T
  } catch (e) {
    logger.warn(`[Storage] JSON 解析失败 (key: ${key}):`, e)
    removeStorage(key)
    return null
  }
}
